import * as React from 'react'
import ModuleTable, { ModuleTableColumn } from '@/features/modules/components/module-table'
import BlueButton from '@/components/button/blue-button'
import { ModulesSidebar } from '@/features/modules/components/sidebar'
import { Main } from '@/components/layout/main'

const formatDateTime = (d: Date) =>
    d.toLocaleDateString('vi-VN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    })

interface StudyTextItem {
    id: string | number
    title: string
    createdAt: Date
}

const studyTextData: StudyTextItem[] = [
    { id: 1, title: 'Definition and issues', createdAt: new Date('2022-11-07T16:35:00') },
    { id: 2, title: 'Definition and issues', createdAt: new Date('2022-10-09T08:00:00') },
]

const StudyTextTable: React.FC = () => {
    const columns: ModuleTableColumn<StudyTextItem>[] = [
        {
            key: 'title',
            header: 'Study text',
            render: (r) => <span className='truncate block max-w-[480px]'>{r.title}</span>,
        },
        { key: 'createdAt', header: 'Created at', render: (r) => formatDateTime(r.createdAt) },
        {
            key: 'actions',
            header: '',
            className: 'w-[140px] text-right',
            render: () => (
                <div className='flex justify-end gap-4'>
                    <button
                        type='button'
                        className='text-sm font-medium text-[#0057B8] hover:underline focus:outline-none'
                    >
                        View
                    </button>
                    <button
                        type='button'
                        className='text-sm font-medium text-[#D92D20] hover:underline focus:outline-none'
                    >
                        Delete
                    </button>
                </div>
            ),
        },
    ]

    return (
        <ModuleTable
            title='Study Text'
            data={studyTextData}
            columns={columns}
            headerAction={<BlueButton text='Add study text' />}
            getRowKey={(r) => r.id}
            ariaLabel='Study Text Table'
        />
    )
}

interface QuestionChapter {
    id: string | number
    chapter: string
    questions: number
}

const questionLibraryAll: QuestionChapter[] = Array.from({ length: 95 }).map((_, i) => ({
    id: i + 1,
    chapter: `Chapter ${i + 1} - Duis malesuada turpis at sapien ullamcorper ornare.`,
    questions: 100,
}))

const QuestionLibraryTable: React.FC = () => {
    const [page, setPage] = React.useState(1)
    const pageSize = 10
    const start = (page - 1) * pageSize
    const pageData = React.useMemo(
        () => questionLibraryAll.slice(start, start + pageSize),
        [page]
    )

    const columns: ModuleTableColumn<QuestionChapter>[] = [
        {
            key: 'chapter',
            header: 'Chapter',
            render: (r) => <span className='truncate block max-w-[560px]'>{r.chapter}</span>,
        },
        { key: 'questions', header: 'Questions' },
        {
            key: 'actions',
            header: '',
            className: 'w-[80px] text-right',
            render: () => (
                <button
                    type='button'
                    className='text-sm font-medium text-[#0057B8] hover:underline focus:outline-none'
                >
                    View
                </button>
            ),
        },
    ]

    return (
        <ModuleTable
            title='Question Library'
            data={pageData}
            columns={columns}
            totalItems={questionLibraryAll.length}
            pagination={{
                page,
                pageSize,
                total: questionLibraryAll.length,
                onPageChange: setPage,
                readOnlyPageDisplay: false,
            }}
            getRowKey={(r) => r.id}
            ariaLabel='Question Library Table'
        />
    )
}

interface MockTestItem {
    id: string | number
    name: string
    createdBy: string
    createdAt: Date
}

const mockTestsAll: MockTestItem[] = Array.from({ length: 33 }).map((_, i) => ({
    id: i + 1,
    name: `In nec lacus auctor, dapibus nisl ${i + 1}.`,
    createdBy: ['Bessie Cooper', 'Albert Flores', 'Floyd Miles', 'Jacob Jones'][i % 4],
    createdAt: new Date(2021, 11, 26 + (i % 7), 8, 0, 1),
}))

const MockTestListTable: React.FC = () => {
    const [page, setPage] = React.useState(3)
    const pageSize = 10
    const start = (page - 1) * pageSize
    const pageData = React.useMemo(
        () => mockTestsAll.slice(start, start + pageSize),
        [page]
    )

    const columns: ModuleTableColumn<MockTestItem>[] = [
        {
            key: 'name',
            header: 'Name',
            render: (r) => <span className='truncate block max-w-[480px]'>{r.name}</span>,
        },
        {
            key: 'createdBy',
            header: 'Created by',
            render: (r) => (
                <div className='flex items-center gap-3'>
                    <div className='h-8 w-8 rounded-full bg-[#E0E7EF] flex items-center justify-center text-xs font-semibold text-[#002652]'>
                        {r.createdBy
                            .split(' ')
                            .map((p) => p[0])
                            .slice(0, 2)
                            .join('')}
                    </div>
                    <span className='text-[#002652]'>{r.createdBy}</span>
                </div>
            ),
        },
        { key: 'createdAt', header: 'Created at', render: (r) => formatDateTime(r.createdAt) },
        {
            key: 'actions',
            header: '',
            className: 'w-[80px] text-right',
            render: () => (
                <button
                    type='button'
                    className='text-sm font-medium text-[#0057B8] hover:underline focus:outline-none'
                >
                    View
                </button>
            ),
        },
    ]

    return (
        <ModuleTable
            title='Mock Test List'
            data={pageData}
            columns={columns}
            totalItems={mockTestsAll.length}
            pagination={{ page, pageSize, total: mockTestsAll.length, onPageChange: setPage }}
            headerAction={<BlueButton text='Create test' />}
            getRowKey={(r) => r.id}
            ariaLabel='Mock Test List Table'
        />
    )
}

export const ModuleDetailPage: React.FC = () => {
    const [active, setActive] = React.useState('study-text')

    let content: React.ReactNode
    switch (active) {
        case 'question-library':
            content = <QuestionLibraryTable />
            break
        case 'mock-test':
            content = <MockTestListTable />
            break
        case 'study-text':
        default:
            content = <StudyTextTable />
    }

    return (
        <Main className='pt-4 flex gap-6 items-start'>
            <ModulesSidebar active={active} onChange={setActive} className='sticky top-0 h-fit' />
            <div className='flex-1 space-y-6'>{content}</div>
        </Main>
    )
}

export default ModuleDetailPage
